import React, { FC, useContext, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { api, Booking, SourceApp } from "../../../../logic/api";
import { SessionContext } from "../../../../utility/useSession";
import { useTranslation } from "react-i18next";
import TextInputField from "../../../../components/editable/EditableField/fields/TextInputField";
import ErrorDialog from "../../../../components/notifications/errorDialog/ErrorDialog";

interface UndoCheckInResponse {
  booking: Booking | null;
  success: boolean;
  error?: string;
}

interface DialogProps {
  booking: Booking | null;
  display: boolean;
  cancel: () => void;
  onUndoCheckIn: (booking: Booking | null, error?: string) => void;
}

const UndoCheckInDialog: FC<DialogProps> = ({ booking, display, cancel, onUndoCheckIn }) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string>("");

  if (!display || !booking) {
    return null;
  }

  function handleResponse(response: UndoCheckInResponse | null) {
    if (!response) return;
    const { booking, success, error } = response;

    if (booking && success) {
      onUndoCheckIn(booking);
      return;
    }

    if (booking && error) {
      onUndoCheckIn(booking, error);
      setError(error);
      return;
    }

    setError("criticalErrorNoBooking");
  }

  return (
    <Dialog maxWidth="md" open={display}>
      {error ? (
        <ErrorDialog
          err={t(`booking.undoCheckInDialog.undoCheckInErrors.${error}`)}
          title={t("Error")}
          onClose={() => {
            setError("");
            cancel();
          }}
        />
      ) : (
        <PromptDialog booking={booking} onCancel={cancel} onResponse={handleResponse} />
      )}
    </Dialog>
  );
};

const PromptDialog: FC<{
  booking: Booking;
  onCancel: () => void;
  onResponse: (response: UndoCheckInResponse | null) => void;
}> = ({ booking, onCancel, onResponse }) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestUndoCheckIn = api.hooks.booking.undoCheckIn();

  const handleSubmit = () => {
    if (!reason.trim()) return;

    setIsSubmitting(true);
    requestUndoCheckIn.send(
      session.current.context,
      {
        id: booking.id,
        reason: reason.trim(),
        undoCheckInSource: SourceApp.frontEnd,
      },
      (response) => {
        onResponse(response);
        setIsSubmitting(false);
      },
    );
  };

  return (
    <div style={{ width: 400 }} data-testid="undo-checkin-dialog">
      <DialogTitle>{t("booking.undoCheckInDialog.undoCheckInPrompt.title")}</DialogTitle>
      <DialogContent>
        <TextInputField
          autoFocus
          maxWidth={350}
          label="Reason"
          value={reason}
          onChange={setReason}
          maxLength={254}
          testId="undo-checkin-reason-input"
        />
        <div style={{ height: 12 }} />
        <DialogContentText>
          {t("booking.undoCheckInDialog.undoCheckInPrompt.content")}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} data-testid="undo-checkin-cancel-button">{t("general.cancel")}</Button>
        <Button onClick={handleSubmit} disabled={isSubmitting || !reason.trim()} data-testid="undo-checkin-confirm-button">
          <b>{t("general.confirm")}</b>
        </Button>
      </DialogActions>
    </div>
  );
};

export default UndoCheckInDialog;
